import { orderService } from '@/services';
import { eventMapper, orderStatus, } from '@/constants';
import { beforeHours } from '@/utils/time';
import Order from '@/view-model/modal-order-info/OrderInfoVM';
import OrderHistoryVM from '@/view-model/modal-order-info/OrderHistoryVM';
import { ServiceDataOrderEvent } from '@/service-data/order-event/ServiceDataOrderEvent';
import { socketPayloadType } from '@/constants/socketPayloadType';
import { BusinessError } from '@/error';
const retrieveGroup = (statusCode) => orderStatus[statusCode]?.group || 'none';
const makeOrderTable = (orderList) => orderList.reduce((acc, it) => {
    const group = retrieveGroup(it.statusCode);
    const order = {
        [it.orderId]: it,
    };
    Object.assign(acc[group] || (acc[group] = {}), order);
    return acc;
}, {});
const requestOrderEventManager = {
    list: [],
    register(orderEvent) {
        this.list.push(orderEvent);
    },
    unregister(id) {
        if (id) {
            this.list = this.list
                .filter(({ orderId }) => orderId !== id);
        }
    },
    find({ orderId, event }) {
        const result = this.list
            .find((orderEvent) => orderEvent.orderId === orderId
            && orderEvent.event === event);
        this.unregister(result?.orderId);
        return !!result;
    },
};
const action = {
    async getAcceptOrderList({ commit }, payload = {}) {
        const statusCode = [
            orderStatus.ACCEPTED.code,
            orderStatus.WORKER_REQUESTED.code,
        ];
        const { list } = await orderService.getOrderList({
            ...payload,
            statusCode,
        });
        const res = list.map((data) => new Order(data));
        const orderTable = makeOrderTable(res);
        orderTable.accept = orderTable.accept ?? {};
        commit('SET_ORDER_TABLE', orderTable);
    },
    async getAllocateOrderList({ commit }, payload = {}) {
        const statusCode = [
            orderStatus.ALLOCATED.code,
            orderStatus.PICKUP_ARRIVED.code,
        ];
        const response = await orderService.getOrderList({
            ...payload,
            statusCode,
        });
        const res = response.list.map((data) => new Order(data));
        const orderTable = makeOrderTable(res);
        orderTable.allocate = orderTable.allocate ?? {};
        commit('SET_ORDER_TABLE', orderTable);
    },
    async getPickupOrderList({ commit }, payload = {}) {
        const statusCode = [
            orderStatus.PICKUP_FINISHED.code,
        ];
        const response = await orderService.getOrderList({
            ...payload,
            statusCode,
        });
        const res = response.list.map((data) => new Order(data));
        const orderTable = makeOrderTable(res);
        orderTable.pickup = orderTable.pickup ?? {};
        commit('SET_ORDER_TABLE', orderTable);
    },
    async getOrderList({ dispatch }) {
        const pageNumber = 1;
        const pageSize = 20_000;
        const time = Date.now();
        const betweenData = {
            field: 'orderedAt',
            start: beforeHours(time, 24),
            end: time,
        };
        const payload = {
            pageNumber,
            pageSize,
            betweenData,
        };
        dispatch('getAcceptOrderList', payload);
        dispatch('getAllocateOrderList', payload);
        dispatch('getPickupOrderList', payload);
    },
    async getOrder({ commit }, payload = {}) {
        const { orderId = 0, } = payload;
        try {
            const response = await orderService.getOrder({
                orderId,
            });
            commit('SET_ORDER', new Order(response.data));
            return response;
        }
        catch (error) {
            if (error instanceof BusinessError) {
                return {
                    statusCode: error.status,
                };
            }
            const statusCode = error.data?.statusCode;
            return {
                statusCode,
            };
        }
    },
    clearOrder({ commit }) {
        commit('SET_ORDER', null);
    },
    /**
     * 재배차
     */
    async reallocateWorker({ commit }, payload = {}) {
        const { orderId = 0, workerCode = '', } = payload;
        const serviceData = new ServiceDataOrderEvent({
            orderId,
            workerCode,
        });
        const { event, order } = await orderService.reallocate(serviceData);
        commit('SET_ORDER_FROM_EVENT', {
            event,
            order,
        });
    },
    /**
     * 배차요청
     */
    async requestWorker({ dispatch }, payload = {}) {
        const { orderId = 0, workerCode = '', } = payload;
        const serviceData = new ServiceDataOrderEvent({
            orderId,
            workerCode,
        });
        const { event, order } = await orderService.workerRequest(serviceData);
        const orderLikeFromEvent = {
            ...order,
            isKeepForEventAction: true,
            payloadType: socketPayloadType.WORKER_REQUEST,
        };
        dispatch('setOrderFromEvent', orderLikeFromEvent);
        requestOrderEventManager.register({
            orderId,
            event,
        });
    },
    async allocateWorker({ dispatch }, payload = {}) {
        const { orderId = 0, workerCode = '', } = payload;
        const serviceData = new ServiceDataOrderEvent({
            orderId,
            workerCode,
        });
        const { event, order } = await orderService.allocate(serviceData);
        const orderLikeFromEvent = {
            ...order,
            isKeepForEventAction: true,
            payloadType: socketPayloadType.ALLOCATED,
        };
        dispatch('setOrderFromEvent', orderLikeFromEvent);
        requestOrderEventManager.register({
            orderId,
            event,
        });
    },
    /**
     * 배차 취소
     */
    async cancelAllocated({ dispatch }, payload = {}) {
        const { orderId = 0, } = payload;
        const serviceData = new ServiceDataOrderEvent({
            orderId,
        });
        const { event, order } = await orderService.allocateCancel(serviceData);
        const orderLikeFromEvent = {
            ...order,
            isKeepForEventAction: true,
            payloadType: socketPayloadType.ALLOCATED_CANCEL,
        };
        dispatch('setOrderFromEvent', orderLikeFromEvent);
        requestOrderEventManager.register({
            orderId,
            event,
        });
    },
    /**
     * 배차 취소 데탑
     */
    async cancelAllocatedForDesktop(_, payload = {}) {
        const { orderId = 0, } = payload;
        const serviceData = new ServiceDataOrderEvent({
            orderId,
        });
        await orderService.allocateCancel(serviceData);
    },
    /**
     * 주문 취소 (배달 취소)
     */
    async cancelAccepted({ dispatch }, payload = {}) {
        const { orderId = 0, cancelReason = '', } = payload;
        const serviceData = new ServiceDataOrderEvent({
            orderId,
            cancelReason,
        });
        const { event, order } = await orderService.cancel(serviceData);
        const orderLikeFromEvent = {
            ...order,
            isKeepForEventAction: true,
            payloadType: socketPayloadType.CANCEL,
        };
        // TODO: 확인 필요
        dispatch('setOrderFromEvent', orderLikeFromEvent);
        requestOrderEventManager.register({
            orderId,
            event,
        });
    },
    async setOrderFromEvent({ commit, rootGetters }, payload = {}) {
        const isPending = rootGetters['app/isPending'];
        const order = {
            ...payload,
            isPending,
        };
        const isSearchOpened = rootGetters['app/isSearchOpened'];
        if (isSearchOpened) {
            commit('ASSIGN_PENDING_ORDER', order);
            return;
        }
        const { orderId, payloadType, } = order;
        if (isPending) {
            const isPendingUpdateOrderEvent = [
                socketPayloadType.PICKUP_EXPECTTIME,
            ].includes(payloadType);
            if (isPendingUpdateOrderEvent) {
                commit('ASSIGN_PENDING_ORDER', order);
                return;
            }
        }
        const event = eventMapper[payloadType];
        if (requestOrderEventManager
            .find({
            orderId,
            event,
        })) {
            return;
        }
        if (socketPayloadType.ALLOCATED_CANCEL === payloadType) {
            const getAgencyShareInfo = rootGetters['hub/getAgencyShareInfo'];
            const agencyShareInfo = getAgencyShareInfo(order);
            order.agencyShareInfo = agencyShareInfo;
        }
        commit('SET_ORDER_FROM_EVENT', {
            event,
            order,
        });
    },
    async mergePendingOrder({ commit }) {
        commit('CLEAN_ORDER_TABLE');
    },
    async adjustPayment({ commit }, payload = {}) {
        const { orderId = 0, payCashAmount = 0, payCardAmount = 0, prePayAmount = 0, isUntact = false, goodsSummary = '', orderGoods = [], } = payload;
        const response = await orderService.adjustPayment({
            orderId,
            payCashAmount,
            payCardAmount,
            prePayAmount,
            isUntact,
            goodsSummary,
            orderGoods,
        });
        const newOrder = new Order(response.data);
        commit('SET_ORDER', newOrder);
        return response.data;
    },
    async changePickupWishAt({ commit }, payload = {}) {
        const { orderId = 0, pickupWishAt = 0, } = payload;
        const response = await orderService.changePickupWishAt({
            orderId,
            pickupWishAt,
        });
        const newOrder = new Order(response.data);
        commit('SET_ORDER', newOrder);
        return response.data;
    },
    async changePickupDelayWishAt({ commit }, payload = {}) {
        const { orderId = 0, pickupDelayWishAt = 0, } = payload;
        const response = await orderService.changePickupDelayWishAt({
            orderId,
            pickupDelayWishAt,
        });
        const newOrder = new Order(response.data);
        commit('SET_ORDER', newOrder);
        return response.data;
    },
    async changeDropInfo({ commit }, payload = {}) {
        const { orderId = 0, siDo = '', siGunGu = '', eupMyeonDong = '', ri = '', mainLandNumber = '', subLandNumber = '', roadName = '', mainBuildingNumber = '', subBuildingNumber = '', buildingName = '', location = {}, addressDetail = '', } = payload;
        const response = await orderService.changeDropInfo({
            orderId,
            siDo,
            siGunGu,
            eupMyeonDong,
            ri,
            mainLandNumber,
            subLandNumber,
            roadName,
            mainBuildingNumber,
            subBuildingNumber,
            buildingName,
            location,
            addressDetail,
        });
        const newOrder = new Order(response.data);
        commit('SET_ORDER', newOrder);
    },
    async getOrderHistory({ commit }, payload = {}) {
        const { orderId = 0, } = payload;
        const res = await orderService.getOrderHistory({
            orderId,
        });
        const newRes = res.data.map((item) => new OrderHistoryVM(item));
        commit('SET_ORDER_HISTORY', newRes);
        return res.data;
    },
    async resetOrderHistory({ commit }) {
        commit('SET_ORDER_HISTORY', []);
    },
    async adjustDeliveryAmount({ commit }, payload = {}) {
        const { orderId = 0, deliveryAmount = 0, } = payload;
        const response = await orderService.adjustDeliveryAmount({
            orderId,
            deliveryAmount,
        });
        const newOrder = new Order(response.data);
        commit('SET_ORDER', newOrder);
        return response.data;
    },
    async addWorkerSupportAmount({ commit }, payload = {}) {
        const { orderId = 0, workerSupportAmount = 0, } = payload;
        const response = await orderService.addWorkerSupportAmount({
            orderId,
            workerSupportAmount,
        });
        const newOrder = new Order(response.data);
        commit('SET_ORDER', newOrder);
        return response.data;
    },
    async requestBaromoneyRemittance({ commit }, payload = {}) {
        const { memo = '', orderId = '', receiverAccountType = '', remittanceBaromoney = '', } = payload;
        const response = await orderService.requestBaromoneyRemittance({
            memo,
            orderId,
            receiverAccountType,
            remittanceBaromoney,
        });
        const { data } = response;
        commit('baromoney/SET_REMITTANCE', data, {
            root: true,
        });
        return data;
    },
    publishOrderHasBeenCompleted({ commit }, orderId) {
        commit('PUBLISH_ORDER_HAS_BEEN_COMPLETED', orderId);
    },
};
export default action;
